/**
 * Image marquee: clone strips + duplicate content for seamless CSS loop.
 * Modeled on assets/marquee.js (MarqueeComponent).
 */

/**
 * @param {(...args: unknown[]) => void} fn
 * @param {number} ms
 */
function debounce(fn, ms) {
  let timeoutId = 0;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), ms);
  };
}

class ShMarqueeSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #wrapper = null;
  /** @type {HTMLElement | null} */
  #content = null;
  /** @type {HTMLElement | null} */
  #strip = null;
  /** @type {number | null} */
  #marqueeWidth = null;
  /** @type {boolean} */
  #reducedMotion = false;
  /** @type {boolean} */
  #resizeBound = false;

  connectedCallback() {
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.#reducedMotion) {
      this.setAttribute('data-reduced-motion', 'true');
      return;
    }

    this.#wrapper = this.querySelector('[data-sh-marquee-wrapper]');
    this.#content = this.querySelector('[data-sh-marquee-content]');
    this.#strip = this.querySelector('[data-sh-marquee-strip]');

    if (!this.#wrapper || !this.#content || !this.#strip) return;

    this.#applyDirection();
    this.#bindPauseHover();

    if (!this.#resizeBound) {
      window.addEventListener('resize', this.#handleResize);
      this.#resizeBound = true;
    }

    void this.#init();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);
    this.#resizeBound = false;
    this.#teardownPauseHover();
  }

  /** Theme editor: section HTML updated in place */
  reinitialize() {
    if (this.#reducedMotion) return;
    this.#wrapper = this.querySelector('[data-sh-marquee-wrapper]');
    this.#content = this.querySelector('[data-sh-marquee-content]');
    this.#strip = this.querySelector('[data-sh-marquee-strip]');
    if (!this.#wrapper || !this.#content || !this.#strip) return;
    this.#applyDirection();
    void this.#init();
  }

  /**
   * Same formula as assets/marquee.js #calculateSpeed:
   * duration (seconds) = sqrt(stripCount) * speedFactor * (animationSpeed / 9000).
   * Baseline 9000 ms matches the marquee-slider default; lower ms speeds up proportionally.
   */
  #calculateDurationSeconds(stripCount) {
    const baselineMs = Number(this.dataset.animationSpeed);
    const safeBaseline = Number.isFinite(baselineMs) && baselineMs > 0 ? baselineMs : 9000;
    const speedFactor = Number(this.dataset.speedFactor);
    const factor = Number.isFinite(speedFactor) && speedFactor > 0 ? speedFactor : 25;
    const n = Math.max(1, stripCount);
    return Math.sqrt(n) * factor * (safeBaseline / 9000);
  }

  #syncDurationFromStrips() {
    const content = this.#content;
    const wrapper = this.#wrapper;
    if (!content || !wrapper) return;
    const n = content.querySelectorAll('[data-sh-marquee-strip]').length;
    const durationSec = this.#calculateDurationSeconds(n);
    wrapper.style.setProperty('--sh-marquee-slider-duration', `${durationSec}s`);
  }

  #applyDirection() {
    if (!this.#wrapper) return;
    const reverse = this.dataset.reverseDirection === 'true';
    this.#wrapper.style.setProperty('--sh-marquee-slider-direction', reverse ? 'reverse' : 'normal');
  }

  #pauseHoverHandlers = {
    /** @param {Event} e */
    enter: (e) => {
      const root = /** @type {HTMLElement} */ (e.currentTarget);
      const w = root.querySelector('[data-sh-marquee-wrapper]');
      if (w instanceof HTMLElement) w.style.animationPlayState = 'paused';
    },
    /** @param {Event} e */
    leave: (e) => {
      const root = /** @type {HTMLElement} */ (e.currentTarget);
      const w = root.querySelector('[data-sh-marquee-wrapper]');
      if (w instanceof HTMLElement) w.style.animationPlayState = 'running';
    },
  };

  #pauseHoverBound = false;

  #bindPauseHover() {
    if (this.dataset.pauseHover !== 'true' || this.#pauseHoverBound) return;
    this.addEventListener('mouseenter', this.#pauseHoverHandlers.enter);
    this.addEventListener('mouseleave', this.#pauseHoverHandlers.leave);
    this.#pauseHoverBound = true;
  }

  #teardownPauseHover() {
    if (!this.#pauseHoverBound) return;
    this.removeEventListener('mouseenter', this.#pauseHoverHandlers.enter);
    this.removeEventListener('mouseleave', this.#pauseHoverHandlers.leave);
    this.#pauseHoverBound = false;
  }

  async #init() {
    const strip = this.#content?.querySelector('[data-sh-marquee-strip]');
    const content = this.#content;
    const wrapper = this.#wrapper;
    if (!strip || !content || !wrapper) return;

    this.#strip = strip;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    this.#resetStrips();
    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#syncDurationFromStrips();
    this.#restartAnimation();
  }

  #resetStrips() {
    const content = this.#content;
    if (!content) return;

    const strips = content.querySelectorAll('[data-sh-marquee-strip]');
    for (let i = strips.length - 1; i >= 1; i--) {
      strips[i]?.remove();
    }

    this.clonedContent?.remove();
  }

  get clonedContent() {
    const content = this.#content;
    const wrapper = this.#wrapper;
    if (!content || !wrapper) return null;
    const last = wrapper.lastElementChild;
    return content !== last ? /** @type {HTMLElement} */ (last) : null;
  }

  /**
   * @returns {Promise<{ numberOfCopies: number, isHorizontalResize: boolean }>}
   */
  #queryNumberOfCopies() {
    const strip = this.#content?.querySelector('[data-sh-marquee-strip]') ?? this.#strip;
    const root = this;

    return new Promise((resolve) => {
      if (!strip) {
        window.setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          io.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: stripWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          const copies = stripWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / stripWidth));

          window.setTimeout(() => {
            resolve({
              numberOfCopies: copies,
              isHorizontalResize,
            });
          }, 0);
        },
        { root }
      );
      io.observe(strip);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const content = this.#content;
    const strip = content?.querySelector('[data-sh-marquee-strip]');
    if (!content || !strip) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      content.appendChild(strip.cloneNode(true));
    }
  }

  #duplicateContent() {
    const content = this.#content;
    const wrapper = this.#wrapper;
    if (!content || !wrapper) return;

    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(clone);
  }

  #restartAnimation() {
    const wrapper = this.#wrapper;
    if (!wrapper) return;
    const animations = wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const anim of animations) {
        anim.currentTime = 0;
      }
    });
  }

  #handleResize = debounce(async () => {
    if (this.#reducedMotion) return;

    const content = this.#content;
    if (!content) return;

    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) return;

    const strips = content.querySelectorAll('[data-sh-marquee-strip]');
    const currentNumberOfCopies = strips.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      const firstStrip = strips[0];
      if (firstStrip) {
        for (let i = 0; i < newNumberOfCopies - currentNumberOfCopies; i++) {
          content.appendChild(firstStrip.cloneNode(true));
        }
      }
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      const toRemove = Math.min(currentNumberOfCopies - newNumberOfCopies, strips.length - 1);
      for (let i = 0; i < toRemove; i++) {
        content.lastElementChild?.remove();
      }
    }

    this.#duplicateContent();
    this.#syncDurationFromStrips();
    this.#restartAnimation();
  }, 250);
}

if (!customElements.get('sh-marquee-slider')) {
  customElements.define('sh-marquee-slider', ShMarqueeSlider);
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  target.querySelectorAll('sh-marquee-slider').forEach((el) => {
    if (el instanceof ShMarqueeSlider && typeof el.reinitialize === 'function') {
      el.reinitialize();
    }
  });
});

